import {
    addMilliseconds,
    differenceInDays,
    isAfter,
    isSameDay,
    max,
    min,
    startOfDay,
    subDays,
} from "date-fns";
import type { Unsubscribe } from "firebase/auth";
import {
    type CollectionReference,
    collection,
    getDoc,
    getDocs,
    onSnapshot,
    orderBy,
    type Query,
    type QueryDocumentSnapshot,
    query,
    where,
} from "firebase/firestore";
import type { AiSummary } from "./common/types/AiSummary";
import type { Conversation } from "./common/types/Conversation";
import type { FirebaseTimestamp } from "./common/types/FirebaseTimestamp";
import type {
    InternalNote,
    Message,
    MessageOrInternalNoteOrAiSummary,
} from "./common/types/Message";
import { excludeUndefined } from "./common/utils/array.util";
import { addIdAndCast } from "./common/utils/data.util";
import { db } from "./Firebase";
import {
    conversationAiSummariesCollection,
    conversationInternalNotesCollection,
    conversationMessagesCollection,
    queryInternalNoteWithRequest,
    queryMessageWithRequest,
} from "./FirebaseBackend";
import { internalNoteDocument, messageDocument } from "./FirebaseBackendActions";
import {
    fromTimestampToDate,
    isFirebaseTimestamp,
    timestampFromDate,
} from "./utils/timestamp.util";

const defaultPageSizeDays = 30;

type GroupedTimelineItems = {
    key: string;
    request: string | null | undefined;
    timelineItems: MessageOrInternalNoteOrAiSummary[];
};

const queryPaginatedByTime = (
    collectionRef: CollectionReference,
    from: Date,
    to: Date | undefined,
) => {
    const constraints = [
        where("sentAt", ">=", from),
        ...(to ? [where("sentAt", "<", to)] : []),
    ];
    return query(collectionRef, orderBy("sentAt", "desc"), ...constraints);
};

const queryConversationMessages = (
    conversation: string,
    from: Date,
    to: Date | undefined,
) =>
    queryPaginatedByTime(
        collection(db, conversationMessagesCollection(conversation)),
        from,
        to,
    );

const queryInternalNotes = (conversation: string, from: Date, to: Date | undefined) =>
    queryPaginatedByTime(
        collection(db, conversationInternalNotesCollection(conversation)),
        from,
        to,
    );

const queryConversationAiSummaries = (
    conversation: string,
    from: Date,
    to: Date | undefined,
) =>
    queryPaginatedByTime(
        collection(db, conversationAiSummariesCollection(conversation)),
        from,
        to,
    );

function sortBySentAt(
    a: { sentAt: FirebaseTimestamp },
    b: { sentAt: FirebaseTimestamp },
) {
    if (!a.sentAt || !b.sentAt) return 0;
    if (fromTimestampToDate(a.sentAt) > fromTimestampToDate(b.sentAt)) return 1;
    if (fromTimestampToDate(a.sentAt) < fromTimestampToDate(b.sentAt)) return -1;
    return 0;
}

function castToMessages(docs: QueryDocumentSnapshot[]) {
    return docs
        .map((doc) => {
            if (doc.exists()) {
                return {
                    id: doc.id,
                    ...doc.data(),
                } as unknown as Message;
            }
            return undefined;
        })
        .filter(excludeUndefined);
}

function castToInternalNotes(docs: QueryDocumentSnapshot[]) {
    return docs
        .map((doc) => {
            if (doc.exists()) {
                return {
                    id: doc.id,
                    ...doc.data(),
                    isInternalNote: true,
                } as unknown as InternalNote;
            }
            return undefined;
        })
        .filter(excludeUndefined);
}

function castToAiSummaries(docs: QueryDocumentSnapshot[]) {
    return docs
        .map((doc) => {
            if (doc.exists()) {
                return {
                    id: doc.id,
                    ...doc.data(),
                } as unknown as AiSummary;
            }
            return undefined;
        })
        .filter(excludeUndefined);
}

async function loadMessages(messageQuery: Query) {
    const getMessages = await getDocs(messageQuery);
    return castToMessages(getMessages.docs);
}

async function loadInternalNotes(notesQuery: Query) {
    const getNotes = await getDocs(notesQuery);
    return castToInternalNotes(getNotes.docs);
}

async function loadAiSummaries(summariesQuery: Query) {
    const getSummaries = await getDocs(summariesQuery);
    return castToAiSummaries(getSummaries.docs);
}

export class ChatPageState {
    private previous: ChatPageState | undefined;

    readonly pageSizeDays: number;

    readonly conversation: Conversation;

    readonly from: Date;

    readonly key = crypto.randomUUID();

    readonly to: Date | undefined;

    public version: number = 0;

    private messages: Message[] | undefined;

    private messagesLoading: Promise<Message[]> | undefined;

    private notes: InternalNote[] | undefined;

    private notesLoading: Promise<InternalNote[]> | undefined;

    private aiSummaries: AiSummary[] | undefined;

    private aiSummariesLoading: Promise<AiSummary[]> | undefined;

    private triggerChanges: () => void;

    private unsubscribeMessages: Unsubscribe | undefined;

    private unsubscribeNotes: Unsubscribe | undefined;

    private unsubscribeAiSummaries: Unsubscribe | undefined;

    constructor(
        conversation: Conversation,
        from: Date,
        to: Date | undefined,
        onChanges: (page: ChatPageState) => void,
        pageSizeDays = defaultPageSizeDays,
    ) {
        this.conversation = conversation;
        this.from = from;
        this.to = to;
        this.triggerChanges = () => {
            onChanges(this);
        };
        this.pageSizeDays = pageSizeDays;
        this.version = 0;
    }

    // TODO: This is a hack to keep the UI in sync with the ChatPageState.
    // This will trigger a re-render in the ChatPage component as its
    // key prop changes when we trigger updates. If we don't do this, the UI
    // will be out of sync and cause weird issues e.g. displaying a loading spinner
    // even when the ChatPageState is loaded.
    //
    // This whole chat implementation should be refactored to be much more simpler e.g. with TanStack Query.
    private incrementVersion() {
        this.version += 1;
        this.triggerChanges();
    }

    id(): string {
        return `chat-page-${this.key}`;
    }

    isLoading(): boolean {
        const isLoadingOnce =
            this.messagesLoading !== undefined ||
            this.notesLoading !== undefined ||
            this.aiSummariesLoading !== undefined;
        const isLoadingSubscriptions =
            this.isSubscribed() &&
            (this.messages === undefined ||
                this.notes === undefined ||
                this.aiSummaries === undefined);
        return isLoadingOnce || isLoadingSubscriptions;
    }

    hasLoaded(): boolean {
        return (
            this.messages !== undefined &&
            this.notes !== undefined &&
            this.aiSummaries !== undefined
        );
    }

    showLoadMore(): boolean {
        if (this.hasLoaded() || this.isLoading()) {
            return false;
        }
        const previousLoaded =
            this.previous && (this.previous.isLoading() || this.previous.hasLoaded());
        const endsToday = this.to ? isSameDay(this.to, new Date()) : false;
        return previousLoaded || endsToday;
    }

    async load() {
        if (!this.messagesLoading) {
            this.messagesLoading = loadMessages(
                queryConversationMessages(this.conversation.id, this.from, this.to),
            );
        }
        if (!this.notesLoading) {
            this.notesLoading = loadInternalNotes(
                queryInternalNotes(this.conversation.id, this.from, this.to),
            );
        }
        if (!this.aiSummariesLoading) {
            this.aiSummariesLoading = loadAiSummaries(
                queryConversationAiSummaries(this.conversation.id, this.from, this.to),
            );
        }
        this.messages = await this.messagesLoading;
        this.notes = await this.notesLoading;
        this.aiSummaries = await this.aiSummariesLoading;
        this.messagesLoading = undefined;
        this.notesLoading = undefined;
        this.aiSummariesLoading = undefined;
        this.incrementVersion();
    }

    conversationCreatedAt(): Date {
        return fromTimestampToDate(
            this.conversation.createdAt || timestampFromDate(new Date()),
        );
    }

    canCreatePreviousPage(): boolean {
        if (this.previous) return this.previous.canCreatePreviousPage();
        const conversationCreatedAt = this.conversationCreatedAt();
        const reachedTheEnd = conversationCreatedAt.getTime() === this.from.getTime();
        if (reachedTheEnd) return false;
        return true;
    }

    calculateFromDate(startFrom: Date | undefined = undefined) {
        const conversationCreatedAt = this.conversationCreatedAt();
        const previousFrom = subDays(
            startOfDay(startFrom || this.from),
            this.pageSizeDays,
        );
        const previousFromCappedToConversationCreation =
            conversationCreatedAt > previousFrom ? conversationCreatedAt : previousFrom;
        return previousFromCappedToConversationCreation;
    }

    createPreviousPage(
        startFrom: Date | undefined = undefined,
    ): ChatPageState | undefined {
        if (this.previous) {
            return this.previous.createPreviousPage(startFrom);
        }
        if (!this.canCreatePreviousPage()) {
            return undefined;
        }
        const newToDate = new Date(this.from.getTime());
        this.previous = new ChatPageState(
            this.conversation,
            this.calculateFromDate(startFrom),
            newToDate,
            this.triggerChanges,
        );
        return this.previous;
    }

    async find(
        messageOrNoteId: string,
        loadOrSubscribe: "find" | "load" | "subscribe",
    ): Promise<boolean> {
        const hasMessageLoaded = this.getAllMessages()
            .map((m) => m.id)
            .includes(messageOrNoteId);
        if (hasMessageLoaded) return true;

        const hasNoteLoaded = this.getAllNotes()
            .map((n) => n.id)
            .includes(messageOrNoteId);
        if (hasNoteLoaded) return true;

        let sentAt: Date | undefined;
        const loadMessage = await getDoc(
            messageDocument(this.conversation.id, messageOrNoteId),
        );

        if (loadMessage.exists()) {
            const sentAtTimeStamp = loadMessage.get("sentAt");

            sentAt = fromTimestampToDate(sentAtTimeStamp);
        } else {
            const loadInternalNote = await getDoc(
                internalNoteDocument(this.conversation.id, messageOrNoteId),
            );
            if (loadInternalNote.exists()) {
                const sentAtTimeStamp = loadInternalNote.get("sentAt");

                sentAt = fromTimestampToDate(sentAtTimeStamp);
            }
        }

        if (!sentAt) return false;

        const page = this.findPageByTime(sentAt);
        switch (loadOrSubscribe) {
            case "load":
                await page?.load();
                break;
            case "subscribe":
                await page?.subscribeToChanges();
                break;
            default:
                break;
        }
        return true;
    }

    async findAndLoad(message: string): Promise<boolean> {
        return this.find(message, "find");
    }

    async findAndSubscribe(message: string) {
        return this.find(message, "subscribe");
    }

    isBetween(date: Date) {
        if (this.to) {
            return this.from <= date && date < this.to;
        }
        return this.from <= date;
    }

    findPageByTime(date: Date): ChatPageState | undefined {
        if (this.isBetween(date)) return this;
        if (this.previous) {
            return this.previous.findPageByTime(date);
        }
        if (this.canCreatePreviousPage()) {
            return this.createPreviousPage()?.findPageByTime(date);
        }
        return undefined;
    }

    createPreviousPageBetweenRange(range: {
        from: Date;
        to: Date;
    }): ChatPageState | undefined {
        this.previous = undefined;

        if (differenceInDays(this.from, range.to) > this.pageSizeDays) {
            return this.createPreviousPage()?.createPreviousPageBetweenRange(range);
        }

        if (range.to < this.from) {
            return this.createPreviousPageBetweenRange({
                from: range.to,
                to: this.from,
            })?.createPreviousPageBetweenRange(range);
        }

        this.previous = new ChatPageState(
            this.conversation,
            range.from,
            range.to,
            this.triggerChanges,
        );
        return this.previous;
    }

    isSubscribed(): boolean {
        return (
            this.unsubscribeMessages !== undefined ||
            this.unsubscribeNotes !== undefined ||
            this.unsubscribeAiSummaries !== undefined
        );
    }

    subscribeToChanges(listenToChanges: (() => void) | undefined = undefined) {
        if (this.unsubscribeMessages) {
            this.unsubscribeMessages();
        }
        const waitForFirstMessages = new Promise((resolve) => {
            this.unsubscribeMessages = onSnapshot(
                queryConversationMessages(this.conversation.id, this.from, this.to),
                { includeMetadataChanges: true },
                (querySnapshot) => {
                    this.messages = castToMessages(querySnapshot.docs);
                    this.incrementVersion();
                    if (listenToChanges) listenToChanges();

                    if (!querySnapshot.metadata.fromCache) {
                        resolve(true);
                    }
                },
            );
        });
        if (this.unsubscribeNotes) {
            this.unsubscribeNotes();
        }
        const waitForFirstNotes = new Promise((resolve) => {
            this.unsubscribeNotes = onSnapshot(
                queryInternalNotes(this.conversation.id, this.from, this.to),
                { includeMetadataChanges: true },
                (querySnapshot) => {
                    this.notes = castToInternalNotes(querySnapshot.docs);
                    this.incrementVersion();
                    if (listenToChanges) listenToChanges();

                    if (!querySnapshot.metadata.fromCache) {
                        resolve(true);
                    }
                },
            );
        });
        if (this.unsubscribeAiSummaries) {
            this.unsubscribeAiSummaries();
        }
        const waitForFirstAiSummaries = new Promise((resolve) => {
            this.unsubscribeAiSummaries = onSnapshot(
                queryConversationAiSummaries(this.conversation.id, this.from, this.to),
                { includeMetadataChanges: true },
                (querySnapshot) => {
                    this.aiSummaries = castToAiSummaries(querySnapshot.docs);
                    this.incrementVersion();
                    if (listenToChanges) listenToChanges();

                    if (!querySnapshot.metadata.fromCache) {
                        resolve(true);
                    }
                },
            );
        });
        return Promise.all([
            waitForFirstMessages,
            waitForFirstNotes,
            waitForFirstAiSummaries,
        ]);
    }

    unsubscribe() {
        if (this.unsubscribeMessages) {
            this.unsubscribeMessages();
            this.unsubscribeMessages = undefined;
        }
        if (this.unsubscribeNotes) {
            this.unsubscribeNotes();
            this.unsubscribeNotes = undefined;
        }
        if (this.unsubscribeAiSummaries) {
            this.unsubscribeAiSummaries();
            this.unsubscribeAiSummaries = undefined;
        }
        this.previous?.unsubscribe();
    }

    getPrevious(): ChatPageState | undefined {
        return this.previous;
    }

    last(): ChatPageState {
        if (this.previous) return this.previous.last();
        return this;
    }

    listAll(): ChatPageState[] {
        return [this, ...(this.previous ? this.previous.listAll() : [])];
    }

    getMessages(): Message[] {
        return this.messages || [];
    }

    getNotes(): InternalNote[] {
        return this.notes || [];
    }

    getAllMessages(): Message[] {
        return [...(this.messages || []), ...(this.previous?.getAllMessages() || [])];
    }

    getAllNotes(): InternalNote[] {
        return [...(this.notes || []), ...(this.previous?.getAllNotes() || [])];
    }

    getAllAiSummaries(): AiSummary[] {
        return [
            ...(this.aiSummaries || []),
            ...(this.previous?.getAllAiSummaries() || []),
        ];
    }

    sortedMessagesNotesAndAiSummaries(): MessageOrInternalNoteOrAiSummary[] {
        return [
            ...(this.messages || []),
            ...(this.notes || []),
            ...(this.aiSummaries || []),
        ].sort(sortBySentAt);
    }

    groupedTimelineItems(
        requestId: string,
        createdFromMessageId: string | null | undefined,
    ): GroupedTimelineItems[] {
        function timelineItemRequestId(item: MessageOrInternalNoteOrAiSummary) {
            if (item.id === createdFromMessageId) {
                return requestId;
            }

            // Handle different request property names across types
            if ("requestId" in item && item.requestId !== undefined) {
                // AiSummary type uses 'requestId'
                return item.requestId;
            } else if ("request" in item && item.request !== undefined) {
                // Message and InternalNote types use 'request'
                return item.request;
            }

            return null;
        }

        return this.sortedMessagesNotesAndAiSummaries().reduce<GroupedTimelineItems[]>(
            (
                prev: GroupedTimelineItems[],
                currentItem: MessageOrInternalNoteOrAiSummary,
                index: number,
            ) => {
                const previousGroup: GroupedTimelineItems | undefined =
                    prev.length === 0 ? undefined : prev[prev.length - 1];

                const previousGroupRequest = previousGroup
                    ? previousGroup.request
                    : undefined;

                const currentItemRequestId = timelineItemRequestId(currentItem);
                const currentItemMatchesRequest = currentItemRequestId === requestId;

                const groupIsSameAsPrevious =
                    previousGroupRequest === currentItemRequestId;
                const previousGroupMatchesRequest = previousGroupRequest === requestId;

                const addToPreviousGroup =
                    groupIsSameAsPrevious ||
                    (!currentItemMatchesRequest && !previousGroupMatchesRequest);

                if (previousGroup && addToPreviousGroup) {
                    return [
                        ...prev.slice(0, prev.length - 1),
                        {
                            key: `grouped-chat-page-${index}`,
                            request: currentItemRequestId,
                            timelineItems: [
                                ...previousGroup.timelineItems,
                                currentItem,
                            ],
                        },
                    ];
                }
                return prev.concat([
                    {
                        key: `grouped-chat-page-${index}`,
                        request: currentItemRequestId,
                        timelineItems: [currentItem],
                    },
                ]);
            },
            [],
        );
    }

    sortOrGroupTimelineItems(
        request: string | undefined,
        createdFromMessageId: string | null | undefined,
    ): GroupedTimelineItems[] {
        if (request) {
            return this.groupedTimelineItems(request, createdFromMessageId);
        }
        return [
            {
                key: `non-grouped-chat-page`,
                request: undefined,
                timelineItems: this.sortedMessagesNotesAndAiSummaries(),
            },
        ];
    }
}

async function getCreatedFromMessageSentAt(
    conversationId: string,
    createdFromMessageId: string | undefined | null,
): Promise<FirebaseTimestamp | undefined> {
    if (!createdFromMessageId) return undefined;
    const doc = await getDoc(messageDocument(conversationId, createdFromMessageId));
    if (!doc.exists) return undefined;
    const data = doc.data();
    if (!data) return undefined;

    return isFirebaseTimestamp(data.sentAt) ? data.sentAt : undefined;
}

async function getRequestRange(
    conversation: Conversation,
    requestId: string,
    createdFromMessageId: string | undefined | null,
): Promise<{ from: Date; to: Date } | undefined> {
    const getMessage = async (take: "first" | "last") =>
        (
            await getDocs(queryMessageWithRequest(conversation.id, requestId, take))
        ).docs.map((doc) => addIdAndCast<Message>(doc))[0];
    const getNote = async (take: "first" | "last") =>
        (
            await getDocs(
                queryInternalNoteWithRequest(conversation.id, requestId, take),
            )
        ).docs.map((doc) => addIdAndCast<InternalNote>(doc))[0];
    const chooseDate = (
        choose: (value: Date[]) => Date,
        timestamps: FirebaseTimestamp[],
    ) => choose(timestamps.map(fromTimestampToDate));

    const firstMessage = await getMessage("first");
    const firstNote = await getNote("first");
    const createdFromMessageSentAt = await getCreatedFromMessageSentAt(
        conversation.id,
        createdFromMessageId,
    );

    const firstMessageTime = chooseDate(
        min,
        [firstMessage?.sentAt, firstNote?.sentAt, createdFromMessageSentAt].filter(
            excludeUndefined,
        ),
    );

    if (!firstMessageTime || Number.isNaN(firstMessageTime.getTime())) return undefined;

    const now = new Date();
    if (isAfter(firstMessageTime, subDays(now, defaultPageSizeDays))) {
        return { from: firstMessageTime, to: now };
    }

    const lastMessage = await getMessage("last");
    const lastNote = await getNote("last");

    const lastMessageTime = chooseDate(
        max,
        [lastMessage?.sentAt, lastNote?.sentAt, createdFromMessageSentAt].filter(
            excludeUndefined,
        ),
    );

    const from = firstMessageTime;

    const to =
        lastMessageTime && !Number.isNaN(lastMessageTime.getTime())
            ? isSameDay(lastMessageTime, now)
                ? now
                : addMilliseconds(lastMessageTime, 1)
            : now;

    return { from, to };
}

export function createPaginatedChatMessageState(props: {
    conversation: Conversation;
    requestId: string | undefined;
    createdFromMessageId: string | undefined | null;
    targetedMessageId: string | undefined;
    onChanges: (root: ChatPageState) => void;
    onNewMessages: () => void;
    afterInitialLoad: (targetedMessage: string | undefined) => void;
}): ChatPageState {
    const {
        conversation,
        requestId,
        createdFromMessageId,
        targetedMessageId,
        onChanges,
        onNewMessages,
        afterInitialLoad,
    } = props;
    const now = new Date();
    const futureMessages = new ChatPageState(conversation, now, undefined, onChanges);
    void futureMessages.subscribeToChanges(onNewMessages);

    const loadInitialData = async () => {
        const requestRange = requestId
            ? await getRequestRange(conversation, requestId, createdFromMessageId)
            : undefined;
        if (requestRange) {
            const pageWithRange =
                futureMessages.createPreviousPageBetweenRange(requestRange);
            await pageWithRange?.subscribeToChanges();
            if (futureMessages.getPrevious() !== pageWithRange) {
                await futureMessages.getPrevious()?.subscribeToChanges();
            }
        } else {
            const loadStartFrom = conversation.lastMessage?.sentAt
                ? fromTimestampToDate(conversation.lastMessage.sentAt)
                : undefined;
            const firstPage = futureMessages.createPreviousPage(loadStartFrom);
            await firstPage?.subscribeToChanges();
        }

        if (targetedMessageId) {
            await futureMessages.findAndSubscribe(targetedMessageId);
        }

        afterInitialLoad(targetedMessageId);
    };

    void loadInitialData();

    return futureMessages;
}
